// Shared data access for marketplace listings.
//
// Every screen that reads or writes a listing goes through here so the column
// names, status rules and image-bucket path live in exactly one place.

import type { SupabaseClient } from '@supabase/supabase-js';

export const LISTING_IMAGE_BUCKET = 'listing-images';

// Top-level categories used in URLs (?cat=…) and stored in listings.category.
export type ListingCategory = 'livestock' | 'machinery' | 'haulage' | 'deadstock' | 'crops-feed';

export interface ListingImage {
  id: string;
  listing_id: string;
  image_url: string;
  sort_order: number | null;
}

export interface Listing {
  id: string;
  seller_id: string;
  category: string;
  sub_category: string | null;
  title: string;
  description: string | null;
  species: string | null;
  breed: string | null;
  age: string | null;
  quantity: number | null;
  assurance_scheme: string | null;
  price: number | null;
  price_type: string | null;
  location_town: string | null;
  county: string | null;
  is_verified_seller: boolean | null;
  is_featured: boolean | null;
  status: string | null;
  view_count: number | null;
  enquiry_count: number | null;
  details: Record<string, string> | null;
  created_at: string | null;
  updated_at: string | null;
  /** When the listing drops off the board. null = it does not expire. */
  expires_at?: string | null;
  /** When the current boost runs out. null = not boosted. */
  boosted_until?: string | null;
  listing_images?: ListingImage[];
  seller?: {
    id: string;
    full_name: string | null;
    business_name: string | null;
    county: string | null;
    cph_verified: boolean | null;
    membership_tier: string | null;
  } | null;
}

const LIST_COLUMNS =
  'id, seller_id, category, sub_category, title, description, species, breed, age, quantity, ' +
  'assurance_scheme, price, price_type, location_town, county, is_verified_seller, is_featured, ' +
  'status, view_count, enquiry_count, details, created_at, updated_at, expires_at, boosted_until, ' +
  'listing_images ( id, listing_id, image_url, sort_order )';

export interface ListingQuery {
  category?: string;
  /** Categories to leave out — used by the catch-all "Farm Equipment" tab. */
  excludeCategories?: string[];
  /** One heading inside a category, e.g. 'grain' within crops-feed. */
  subCategory?: string;
  /** One species, or several to OR together (the sidebar's tick boxes). */
  species?: string | string[];
  county?: string;
  /** Free-text search across title, description and breed. */
  q?: string;
  featuredFirst?: boolean;
  minPrice?: number;
  maxPrice?: number;
  verifiedOnly?: boolean;
  sellerId?: string;
  /** Defaults to 'active'. Pass null to ignore status (e.g. a seller's own list). */
  status?: string | null;
  limit?: number;
  offset?: number;
  sort?: 'newest' | 'oldest' | 'price-asc' | 'price-desc' | 'popular';
}

/**
 * Housekeeping: retire listings and boosts whose date has passed.
 *
 * There is no cron on the box, so this rides along with browsing instead. It is
 * throttled because a browse page fires several queries as filters change, and
 * a listing going out of date is not second-sensitive — a minute is plenty.
 * Failures are ignored on purpose: expiry is tidy-up, not something a shopper
 * should ever see an error about.
 */
let lastSweep = 0;
const SWEEP_EVERY_MS = 60_000;

async function sweepExpired(supabase: SupabaseClient): Promise<void> {
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (lastSweep && now - lastSweep < SWEEP_EVERY_MS) return;
  lastSweep = now || 1;
  try {
    await Promise.all([
      supabase.rpc('expire_due_listings'),
      supabase.rpc('expire_due_boosts'),
    ]);
  } catch {
    /* tidy-up only */
  }
}

/** Browse/search listings. Returns rows plus the total count for paging. */
export async function fetchListings(
  supabase: SupabaseClient,
  opts: ListingQuery = {}
): Promise<{ listings: Listing[]; total: number }> {
  const {
    category,
    excludeCategories,
    subCategory,
    species,
    county,
    q,
    minPrice,
    maxPrice,
    verifiedOnly,
    sellerId,
    status = 'active',
    limit = 24,
    offset = 0,
    sort = 'newest',
    featuredFirst = true,
  } = opts;

  // Before reading the live board, drop anything that has run out of time —
  // otherwise an expired listing shows for one more page load.
  if (status === 'active') await sweepExpired(supabase);

  let query = supabase.from('listings').select(LIST_COLUMNS, { count: 'exact' });

  if (status) query = query.eq('status', status);
  if (category) query = query.eq('category', category);
  if (excludeCategories?.length) query = query.not('category', 'in', `(${excludeCategories.join(',')})`);
  if (subCategory) query = query.eq('sub_category', subCategory);
  if (Array.isArray(species)) {
    if (species.length) query = query.in('species', species);
  } else if (species) {
    query = query.eq('species', species);
  }
  if (county) query = query.eq('county', county);
  if (sellerId) query = query.eq('seller_id', sellerId);
  if (verifiedOnly) query = query.eq('is_verified_seller', true);
  if (typeof minPrice === 'number') query = query.gte('price', minPrice);
  // A max price shouldn't hide POA listings — those have no price to compare.
  if (typeof maxPrice === 'number' && !Number.isNaN(maxPrice)) {
    query = query.or(`price.lte.${maxPrice},price.is.null`);
  }

  if (q?.trim()) {
    // Escape the PostgREST or() separators so a search term can't break the filter.
    const term = q.trim().replace(/[,()]/g, ' ');
    query = query.or(
      `title.ilike.%${term}%,description.ilike.%${term}%,breed.ilike.%${term}%,sub_category.ilike.%${term}%`
    );
  }

  if (featuredFirst) query = query.order('is_featured', { ascending: false, nullsFirst: false });

  if (sort === 'price-asc') query = query.order('price', { ascending: true, nullsFirst: false });
  else if (sort === 'price-desc') query = query.order('price', { ascending: false, nullsFirst: false });
  else if (sort === 'popular') query = query.order('view_count', { ascending: false, nullsFirst: false });
  else query = query.order('created_at', { ascending: sort === 'oldest' });

  const { data, error, count } = await query.range(offset, offset + limit - 1);
  if (error) throw error;

  return { listings: (data as unknown as Listing[]) ?? [], total: count ?? 0 };
}

/** One listing with its images and the seller's public profile. */
export async function fetchListing(
  supabase: SupabaseClient,
  id: string
): Promise<Listing | null> {
  const { data, error } = await supabase
    .from('listings')
    .select(
      // NB: no phone here. Seller contact details only ever come out of
      // reveal_contact(), which meters them against the member's allowance.
      `${LIST_COLUMNS}, seller:profiles!listings_seller_id_fkey ( id, full_name, business_name, county, cph_verified, membership_tier )`
    )
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return (data as unknown as Listing) ?? null;
}

export interface NewListingInput {
  category: string;
  sub_category?: string | null;
  title: string;
  description?: string | null;
  species?: string | null;
  breed?: string | null;
  age?: string | null;
  quantity?: number | null;
  assurance_scheme?: string | null;
  price?: number | null;
  price_type?: string | null;
  location_town?: string | null;
  county?: string | null;
  details?: Record<string, string>;
}

/**
 * New listings start here, not on the marketplace: every listing is approved by
 * hand in the admin console, against a 48 hour target.
 *
 * This value is only a hint — the real decision is made in the database by the
 * listings_enforce_review trigger, which reads site_settings.auto_approve_listings.
 * Flipping that switch in the admin console makes new listings go live instantly
 * without any code change, and a seller cannot bypass it from the browser.
 */
export const NEW_LISTING_STATUS = 'pending';

/**
 * Create a listing owned by the signed-in user, then upload any photos and
 * attach them. Images are uploaded after the row exists so they can be filed
 * under the listing id and cleaned up with it.
 */
export async function createListing(
  supabase: SupabaseClient,
  input: NewListingInput,
  files: File[] = []
): Promise<Listing> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) throw new Error('You need to be signed in to post a listing.');

  const { data: listing, error } = await supabase
    .from('listings')
    .insert({
      ...input,
      seller_id: user.id,
      status: NEW_LISTING_STATUS,
    })
    .select('id')
    .single();

  if (error) throw error;

  if (files.length) {
    await uploadListingImages(supabase, listing.id, files);
  }

  return listing as unknown as Listing;
}

/** Upload photos to storage and record them against the listing, in order. */
export async function uploadListingImages(
  supabase: SupabaseClient,
  listingId: string,
  files: File[],
  startSortOrder = 0
): Promise<ListingImage[]> {
  const rows: { listing_id: string; image_url: string; sort_order: number }[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
    const path = `${listingId}/${startSortOrder + i}-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(LISTING_IMAGE_BUCKET)
      .upload(path, file, { cacheControl: '31536000', upsert: false });

    // One bad photo shouldn't lose the whole listing — skip it and carry on.
    if (uploadError) continue;

    const {
      data: { publicUrl },
    } = supabase.storage.from(LISTING_IMAGE_BUCKET).getPublicUrl(path);

    rows.push({ listing_id: listingId, image_url: publicUrl, sort_order: startSortOrder + i });
  }

  if (!rows.length) return [];

  const { data, error } = await supabase.from('listing_images').insert(rows).select();
  if (error) throw error;
  return (data as unknown as ListingImage[]) ?? [];
}

/**
 * Send an enquiry to a listing's seller.
 *
 * Returns the new enquiry's id so the caller can ask the server to email the
 * seller about it — without the id there is nothing to point a notification at.
 */
export async function createEnquiry(
  supabase: SupabaseClient,
  listingId: string,
  message: string
): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('You need to be signed in to send an enquiry.');

  const { data, error } = await supabase
    .from('enquiries')
    .insert({ listing_id: listingId, buyer_id: user.id, message })
    .select('id')
    .single();
  if (error) throw error;

  // Best effort — the counter is a nicety, not worth failing the enquiry over.
  await supabase.rpc('increment_enquiry_count', { listing: listingId }).then(
    () => undefined,
    () => undefined
  );

  return data.id as string;
}

/**
 * Ask the server to email the seller about an enquiry that has just been saved.
 *
 * Deliberately swallows everything. The enquiry is already recorded and the
 * buyer has been told it was sent; a mail problem is ours to find in the admin
 * screen (enquiries.email_error), not something to put in front of a farmer.
 */
export async function notifySellerOfEnquiry(enquiryId: string): Promise<void> {
  try {
    await fetch('/api/notify-enquiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enquiryId }),
    });
  } catch {
    /* nothing the buyer can do about it */
  }
}

/** Ask the server to tell the admin team a listing is waiting for review. */
export async function notifyListingSubmitted(listingId: string): Promise<void> {
  try {
    await fetch('/api/notify-listing-submitted', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listingId }),
    });
  } catch {
    /* the listing is saved either way */
  }
}

/** Bump the view counter. Silently ignored if it fails. */
export async function recordListingView(supabase: SupabaseClient, listingId: string) {
  await supabase.rpc('increment_view_count', { listing: listingId }).then(
    () => undefined,
    () => undefined
  );
}

/** First image for a listing, or null. Sorted so the seller's choice wins. */
export function primaryImage(listing: Listing): string | null {
  const images = [...(listing.listing_images ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );
  return images[0]?.image_url ?? null;
}

/** "£1,250" / "£4.20 per kg" / "POA" — how price is shown across the site. */
export function formatPrice(listing: Pick<Listing, 'price' | 'price_type'>): string {
  if (listing.price_type === 'poa' || listing.price == null) return 'POA';
  const amount = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Number.isInteger(Number(listing.price)) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(listing.price));

  switch (listing.price_type) {
    case 'perhead':
      return `${amount} per head`;
    case 'perkg':
      return `${amount} per kg`;
    case 'pertonne':
      return `${amount} per tonne`;
    case 'perbale':
      return `${amount} per bale`;
    default:
      return amount;
  }
}

/** "2 days ago" style relative age for listing cards. */
export function listedAgo(createdAt: string | null): string {
  if (!createdAt) return '';
  const diff = Date.now() - new Date(createdAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/**
 * Withdraw one of your own listings — the dashboard's "Remove".
 *
 * Goes through a function because status is no longer writable by members
 * directly: it was possible to self-approve a listing by PATCHing it. The
 * listing is marked withdrawn rather than deleted, so its enquiries survive.
 */
export async function withdrawListing(
  supabase: SupabaseClient,
  listingId: string
): Promise<void> {
  const { error } = await supabase.rpc('withdraw_listing', { listing: listingId });
  if (error) throw error;
}

/** Mark one of your own listings as sold. */
export async function markListingSold(
  supabase: SupabaseClient,
  listingId: string
): Promise<void> {
  const { error } = await supabase.rpc('mark_listing_sold', { listing: listingId });
  if (error) throw error;
}
